import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import BrandHeader from "@/components/BrandHeader";
import Footer from "@/components/Footer";
import { Fuel, ArrowRight, ShieldCheck, Zap, Clock, CreditCard, Landmark, XCircle, User } from "lucide-react";

const HERO_BG =
  "https://images.pexels.com/photos/20398056/pexels-photo-20398056.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1600";

export default function Landing() {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState({
    price_per_liter: 2.5,
    zelle_email: "",
    stripe_fee_percent: 0.029,
    stripe_fee_fixed: 0.3,
    zelle_enabled: true,
    stripe_enabled: true,
    fuel_types: [],
  });
  const [form, setForm] = useState({ name: "", email: "", phone: "", liters: "" });
  const [method, setMethod] = useState("zelle");
  const [fuelKey, setFuelKey] = useState("regular");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    api
      .get("/config/public")
      .then((r) => {
        setCfg(r.data);
        if (r.data.zelle_enabled === false && r.data.stripe_enabled) setMethod("stripe");
        else if (r.data.stripe_enabled === false && r.data.zelle_enabled) setMethod("zelle");
        // Preselect first active fuel type
        const first = (r.data.fuel_types || []).find((f) => f.active !== false);
        if (first) setFuelKey(first.key);
      })
      .catch(() => {});
  }, []);

  const selectedFuel = (cfg.fuel_types || []).find((f) => f.key === fuelKey);
  const effectivePrice = selectedFuel ? selectedFuel.price : cfg.price_per_liter;
  const litersNum = parseFloat(form.liters) || 0;
  const subtotal = litersNum * effectivePrice;
  // Gross-up: fee = total − subtotal, where total = (subtotal + FIXED)/(1 − PERCENT).
  // Guarantees the seller receives exactly `subtotal` after Stripe deducts its fee.
  const fee =
    method === "stripe" && subtotal > 0
      ? Math.round(
          ((subtotal + cfg.stripe_fee_fixed) / (1 - cfg.stripe_fee_percent) - subtotal) * 100,
        ) / 100
      : 0;
  const total = Math.round((subtotal + fee) * 100) / 100;
  const salesClosed = cfg.zelle_enabled === false && cfg.stripe_enabled === false;

  const updateField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim() || form.name.trim().length < 2) errs.name = "Ingresa tu nombre completo";
    if (!form.email.trim()) errs.email = "Ingresa tu correo electrónico";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = "Correo inválido";
    if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 4) errs.phone = "Ingresa tu teléfono";
    if (!form.liters || litersNum <= 0) errs.liters = "Ingresa cuántos litros quieres";
    if (!acceptedTerms) errs.terms = "Debes aceptar los Términos y Condiciones";
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      const firstKey = Object.keys(errs)[0];
      toast.error(
        Object.keys(errs).length === 1
          ? errs[firstKey]
          : `Faltan ${Object.keys(errs).length} campos por completar`,
      );
      // Scroll & focus the first missing field
      const el = document.querySelector(`[data-testid="input-${firstKey}"], [data-testid="accept-terms-checkbox"]`);
      if (el && typeof el.focus === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setTimeout(() => el.focus(), 250);
      }
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/orders", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        liters: litersNum,
        payment_method: method,
        fuel_type: fuelKey,
      });
      if (method === "stripe" && data.stripe_checkout_url) {
        // On mobile browsers `window.open` is frequently blocked as a pop-up,
        // leaving the customer on the app without ever seeing Stripe Checkout.
        // Redirect the current tab instead — Stripe will send them back via
        // success_url once payment completes.
        const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
        if (isMobile) {
          window.location.href = data.stripe_checkout_url;
        } else {
          // Desktop keeps the new-tab UX so the customer can compare details.
          const win = window.open(data.stripe_checkout_url, "_blank", "noopener");
          if (!win || win.closed) {
            // Pop-up blocked even on desktop → fall back to same-tab redirect.
            window.location.href = data.stripe_checkout_url;
            return;
          }
          navigate(`/order/${data.id}`);
        }
      } else {
        navigate(`/order/${data.id}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al crear la orden");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white relative overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-40"
        style={{ backgroundImage: `url(${HERO_BG})` }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/70 via-zinc-950/85 to-zinc-950" aria-hidden />
      <div className="absolute inset-0 grid-lines opacity-40" aria-hidden />

      <div className="relative z-10">
        <BrandHeader
          right={
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                className="h-11 px-5 gap-2 rounded-full border border-yellow-400/50 bg-yellow-400/10 text-yellow-300 font-semibold hover:bg-yellow-400/20 hover:text-yellow-200 sm:h-9 sm:px-3 sm:rounded-md sm:border-0 sm:bg-transparent sm:text-zinc-300 sm:font-normal sm:hover:text-yellow-400 sm:hover:bg-white/5"
                onClick={() => navigate("/login")}
                data-testid="customer-login-btn"
              >
                <User className="w-4 h-4 sm:hidden" strokeWidth={2.5} />
                Mi cuenta
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-full text-zinc-500 hover:text-yellow-400 hover:bg-white/5 sm:hidden"
                onClick={() => navigate("/login")}
                data-testid="staff-login-btn-mobile"
                title="Acceso Staff"
                aria-label="Acceso Staff"
              >
                <ShieldCheck className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                className="hidden sm:inline-flex text-zinc-500 hover:text-yellow-400 hover:bg-white/5 text-xs"
                onClick={() => navigate("/login")}
                data-testid="staff-login-btn"
              >
                Staff
              </Button>
            </div>
          }
        />

        <main className="max-w-7xl mx-auto px-4 md:px-12 pt-10 md:pt-24 pb-12 md:pb-16 grid lg:grid-cols-5 gap-8 md:gap-12">
          <section className="lg:col-span-3 flex flex-col justify-center">
            <div
              className={`inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full border ${
                salesClosed
                  ? "border-red-500/40 bg-red-500/10"
                  : "border-yellow-400/30 bg-yellow-400/10"
              } mb-8`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  salesClosed ? "bg-red-400" : "bg-yellow-400 pulse-dot"
                }`}
              />
              <span
                className={`text-xs font-mono-display tracking-widest uppercase ${
                  salesClosed ? "text-red-300" : "text-yellow-300"
                }`}
              >
                {salesClosed ? "Ventas cerradas temporalmente" : "Disponible ahora · Gasolina B94"}
              </span>
            </div>

            <h1 className="font-display font-black tracking-tighter text-5xl md:text-6xl lg:text-7xl leading-[0.95]">
              Paga como quieras.<br />
              Recibe tu código.<br />
              <span className="text-yellow-400">Reposta.</span>
            </h1>
            <p className="mt-6 text-lg text-zinc-400 max-w-xl leading-relaxed">
              Reserva litros de gasolina <span className="text-white font-medium">B94</span> en segundos.
              Paga con <span className="text-white font-medium">Zelle</span> o{" "}
              <span className="text-white font-medium">tarjeta</span>. Al confirmarse tu pago recibirás
              un código de 6 letras para retirar en la estación.
            </p>

            <div className="grid grid-cols-3 gap-4 mt-12 max-w-xl">
              <Feature icon={Zap} title="Al instante" desc="Confirmación automática" />
              <Feature icon={ShieldCheck} title="Seguro" desc="Zelle o Stripe" />
              <Feature icon={Clock} title="24/7" desc="Sin filas ni esperas" />
            </div>
          </section>

          <section className="lg:col-span-2">
            <div className="glass rounded-xl p-8 md:p-10 shadow-2xl shadow-black/50">
              <div className="flex items-center gap-3 mb-1">
                <Fuel className="w-4 h-4 text-yellow-400" />
                <span className="text-[11px] uppercase tracking-[0.3em] text-yellow-400 font-mono-display">
                  Nueva orden
                </span>
              </div>
              <h2 className="font-display font-bold text-2xl mb-6 text-white">Reserva tus litros</h2>

              {salesClosed ? (
                <div
                  data-testid="sales-closed-banner"
                  className="border border-red-500/40 bg-red-500/10 rounded-lg p-5 text-center"
                >
                  <div className="w-12 h-12 mx-auto rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center mb-3">
                    <XCircle className="w-6 h-6 text-red-400" />
                  </div>
                  <div className="font-display font-black text-xl text-red-200 mb-2">
                    Ventas cerradas temporalmente
                  </div>
                  <p className="text-sm text-red-200/80 leading-relaxed">
                    No hay disponibilidad de venta en este momento.
                    Vuelve a intentarlo más tarde — estamos actualizando el suministro.
                  </p>
                  <p className="text-[11px] text-zinc-500 mt-4 font-mono-display uppercase tracking-widest">
                    Serás notificado cuando reabramos
                  </p>
                </div>
              ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="order-form" noValidate>
                {(cfg.fuel_types || []).length > 0 && (
                  <div data-testid="fuel-type-selector">
                    <div className="text-xs uppercase tracking-widest text-zinc-400 font-mono-display mb-2">
                      Tipo de combustible
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {(cfg.fuel_types || []).filter((f) => f.active !== false).map((f) => {
                        const isSelected = fuelKey === f.key;
                        return (
                          <button
                            key={f.key}
                            type="button"
                            onClick={() => setFuelKey(f.key)}
                            data-testid={`fuel-${f.key}-btn`}
                            className={`text-left rounded-lg border p-3 transition ${
                              isSelected
                                ? "border-yellow-400 bg-yellow-400/10 ring-2 ring-yellow-400/40"
                                : "border-white/10 bg-zinc-950 hover:border-yellow-400/40"
                            }`}
                          >
                            <div className={`font-bold text-sm ${isSelected ? "text-yellow-300" : "text-white"}`}>
                              {f.label}
                            </div>
                            {f.octane && (
                              <div className="text-[10px] text-zinc-500 uppercase tracking-wider mt-0.5">
                                {f.octane}
                              </div>
                            )}
                            <div className="font-mono-display text-yellow-400 font-bold text-lg mt-1">
                              ${Number(f.price).toFixed(2)}
                              <span className="text-xs text-zinc-500 font-normal">/L</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                <FormField label="Nombre completo" htmlFor="name" error={errors.name}>
                  <Input
                    id="name"
                    data-testid="input-name"
                    value={form.name}
                    onChange={(e) => updateField("name", e.target.value)}
                    placeholder="Juan Pérez"
                    aria-invalid={!!errors.name}
                    className={fieldClass(errors.name)}
                  />
                </FormField>
                <FormField label="Correo electrónico" htmlFor="email" error={errors.email}>
                  <Input
                    id="email"
                    data-testid="input-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => updateField("email", e.target.value)}
                    placeholder="tucorreo@ejemplo.com"
                    aria-invalid={!!errors.email}
                    className={fieldClass(errors.email)}
                  />
                </FormField>
                <FormField label="Teléfono" htmlFor="phone" error={errors.phone}>
                  <Input
                    id="phone"
                    data-testid="input-phone"
                    value={form.phone}
                    onChange={(e) => updateField("phone", e.target.value)}
                    placeholder="+1 305 555 0100"
                    aria-invalid={!!errors.phone}
                    className={fieldClass(errors.phone)}
                  />
                </FormField>
                <FormField label="Litros" htmlFor="liters" error={errors.liters}>
                  <Input
                    id="liters"
                    data-testid="input-liters"
                    type="number"
                    step="0.01"
                    min="0.1"
                    value={form.liters}
                    onChange={(e) => updateField("liters", e.target.value)}
                    placeholder="Ej. 40"
                    aria-invalid={!!errors.liters}
                    className={fieldClass(errors.liters)}
                  />
                </FormField>

                <div className="space-y-2 pt-1">
                  <Label className="text-xs uppercase tracking-widest text-zinc-400 font-mono-display">
                    Método de pago
                  </Label>
                  <div
                    className={`grid gap-2 ${
                      cfg.zelle_enabled !== false && cfg.stripe_enabled !== false
                        ? "grid-cols-2"
                        : "grid-cols-1"
                    }`}
                  >
                    {cfg.zelle_enabled !== false && (
                      <MethodButton
                        active={method === "zelle"}
                        onClick={() => setMethod("zelle")}
                        testid="method-zelle-btn"
                        icon={Landmark}
                        label="Zelle"
                        sub="Sin fee"
                      />
                    )}
                    {cfg.stripe_enabled !== false && (
                      <MethodButton
                        active={method === "stripe"}
                        onClick={() => setMethod("stripe")}
                        testid="method-stripe-btn"
                        icon={CreditCard}
                        label="Tarjeta"
                        sub="2.9% + $0.30"
                      />
                    )}
                  </div>
                  {method === "zelle" && (
                    <div
                      data-testid="zelle-instructions-note"
                      className="mt-3 rounded-md border border-yellow-400/30 bg-yellow-400/5 px-3 py-2.5 text-xs text-yellow-100/90 leading-relaxed"
                    >
                      <div className="font-bold text-yellow-300 uppercase tracking-wider text-[11px] mb-1">
                        Importante · Confirmación automática Zelle
                      </div>
                      Al enviar tu Zelle debes introducir <span className="font-semibold text-yellow-200">exactamente</span> el
                      mismo <span className="font-semibold text-yellow-200">nombre</span>,{" "}
                      <span className="font-semibold text-yellow-200">apellido</span> y{" "}
                      <span className="font-semibold text-yellow-200">cantidad</span> que registras en el portal.
                      Así el sistema confirma tu pago y libera el código automáticamente.
                      <br />
                      Si los datos no coinciden, deberás verificarlo <span className="font-semibold text-yellow-200">manualmente</span>{" "}
                      desde tu registro en el portal (<span className="whitespace-nowrap">Mi cuenta → Verificar pago</span>)
                      introduciendo los datos tal como salieron en tu Zelle.
                    </div>
                  )}
                </div>

                <div className="pt-3 border-t border-white/5 space-y-1.5">
                  <SummaryLine label="Subtotal" value={subtotal} testid="subtotal-display" />
                  {method === "stripe" && (
                    <SummaryLine
                      label="Fee tarjeta (Stripe)"
                      value={fee}
                      testid="fee-display"
                      muted
                    />
                  )}
                  <div className="flex items-baseline justify-between pt-2">
                    <span className="text-sm text-zinc-500 uppercase tracking-widest font-mono-display">
                      Total
                    </span>
                    <span
                      className="font-display font-black text-3xl text-yellow-400"
                      data-testid="order-total-display"
                    >
                      ${total.toFixed(2)}
                    </span>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="submit-order-btn"
                  className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold h-12 text-base transition-transform duration-150 hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
                >
                  {loading
                    ? "Procesando..."
                    : method === "stripe"
                    ? "Pagar con tarjeta"
                    : "Continuar al pago Zelle"}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>

                <label
                  htmlFor="accept-terms"
                  className={`flex items-start gap-2.5 rounded-md border px-3 py-2.5 cursor-pointer transition-colors ${
                    acceptedTerms
                      ? "border-yellow-400/40 bg-yellow-400/5"
                      : "border-white/10 bg-zinc-900/50 hover:border-white/20"
                  }`}
                >
                  <input
                    id="accept-terms"
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    data-testid="accept-terms-checkbox"
                    className="mt-0.5 h-4 w-4 accent-yellow-400 cursor-pointer"
                    aria-label="Acepto los Términos y Condiciones"
                  />
                  <span className="text-xs text-zinc-400 leading-relaxed">
                    Acepto los{" "}
                    <a
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="landing-terms-link"
                      className="text-yellow-400 hover:underline font-semibold"
                      onClick={(ev) => ev.stopPropagation()}
                    >
                      Términos y Condiciones
                    </a>{" "}
                    del servicio, incluyendo la política de cancelación y despacho de combustible.
                  </span>
                </label>
              </form>
              )}
            </div>

            <p className="text-center text-xs text-zinc-500 mt-6 font-mono-display tracking-wider">
              ZELLE (SIN FEE) · TARJETA VIA STRIPE (2.9% + $0.30)
            </p>
          </section>
        </main>

        <Footer />
      </div>
    </div>
  );
}

function Feature({ icon: Icon, title, desc }) {
  return (
    <div className="border-l-2 border-yellow-400/40 pl-3">
      <Icon className="w-4 h-4 text-yellow-400 mb-2" />
      <div className="font-display font-bold text-white text-sm">{title}</div>
      <div className="text-xs text-zinc-500">{desc}</div>
    </div>
  );
}

function FormField({ label, htmlFor, children, error }) {
  return (
    <div className="space-y-1.5">
      <Label
        htmlFor={htmlFor}
        className={`text-xs uppercase tracking-widest font-mono-display flex items-center gap-1.5 ${
          error ? "text-red-400" : "text-zinc-400"
        }`}
      >
        {label}
        {error && <span className="text-red-500">*</span>}
      </Label>
      {children}
      {error && (
        <p
          className="text-[11px] text-red-400 font-medium flex items-center gap-1"
          data-testid={`error-${htmlFor}`}
        >
          <span className="inline-block w-1 h-1 rounded-full bg-red-500" />
          {error}
        </p>
      )}
    </div>
  );
}

function fieldClass(hasError) {
  const base =
    "bg-zinc-900 text-white placeholder:text-zinc-600 focus-visible:ring-yellow-400 transition-colors";
  return hasError
    ? `${base} border-red-500/70 focus-visible:border-red-400 focus-visible:ring-red-400/60 animate-shake`
    : `${base} border-white/10 focus-visible:border-yellow-400`;
}

function MethodButton({ active, onClick, testid, icon: Icon, label, sub }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`relative flex flex-col items-start gap-1 rounded-md border p-3 text-left transition-colors duration-150 ${
        active
          ? "border-yellow-400 bg-yellow-400/10"
          : "border-white/10 bg-zinc-900 hover:border-white/20"
      }`}
    >
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${active ? "text-yellow-400" : "text-zinc-400"}`} />
        <span className={`font-display font-bold ${active ? "text-white" : "text-zinc-300"}`}>
          {label}
        </span>
      </div>
      <span className="text-[10px] uppercase tracking-widest font-mono-display text-zinc-500">
        {sub}
      </span>
      {active && (
        <span className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-yellow-400" />
      )}
    </button>
  );
}

function SummaryLine({ label, value, testid, muted = false }) {
  return (
    <div className="flex items-baseline justify-between">
      <span
        className={`text-xs uppercase tracking-widest font-mono-display ${
          muted ? "text-zinc-500" : "text-zinc-400"
        }`}
      >
        {label}
      </span>
      <span
        className={`font-mono-display ${muted ? "text-zinc-400 text-sm" : "text-white"}`}
        data-testid={testid}
      >
        ${value.toFixed(2)}
      </span>
    </div>
  );
}
